import { useEffect, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { API_BASE, useAuth } from "../components/AuthContext";
import { User as UserIcon, Save, Clock, Eye, X, Send } from "lucide-react";
import EmailSignatureSection from "../components/EmailSignatureSection";

// v68.24 — defense-in-depth: sanitize the digest-preview HTML at render even
// though it's rendered from our own template — a compromised sender or a bug
// in the template must not lead to script execution.
const DIGEST_HTML_CFG = {
  ALLOWED_TAGS: ["a", "b", "br", "div", "em", "h1", "h2", "h3", "h4", "hr",
                  "i", "img", "li", "ol", "p", "span", "strong", "table", "tbody",
                  "td", "th", "thead", "tr", "u", "ul"],
  ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "style", "width",
                  "height", "colspan", "rowspan"],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|data:image\/(?:png|jpeg|gif|webp|svg\+xml);base64,):|\/|#)/i,
};

// Common IANA time zones — keeps the dropdown manageable. Users can still
// land on others via the API; this is the curated demo set.
const TIMEZONES = [
  "Australia/Sydney", "Australia/Melbourne", "Australia/Brisbane",
  "Australia/Perth", "Australia/Adelaide", "Pacific/Auckland",
  "Asia/Singapore", "Asia/Hong_Kong", "Asia/Tokyo", "Asia/Kolkata",
  "Asia/Dubai", "Europe/London", "Europe/Paris", "Europe/Berlin",
  "Europe/Amsterdam", "Africa/Johannesburg",
  "America/New_York", "America/Chicago", "America/Denver",
  "America/Los_Angeles", "America/Toronto", "America/Vancouver",
  "America/Sao_Paulo", "UTC",
];

function guessBrowserTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; }
}

export default function Profile() {
  const { state, refresh } = useAuth();
  const me = state?.user;
  const [name, setName] = useState(me?.name || "");
  const [tz, setTz] = useState(me?.time_zone || "");
  const [tan, setTan] = useState(me?.tax_agent_number || "");
  const [digest, setDigest] = useState(me?.task_digest_enabled !== false);
  const [idReport, setIdReport] = useState(me?.id_report_enabled !== false);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    setName(me?.name || "");
    setTz(me?.time_zone || "");
    setTan(me?.tax_agent_number || "");
    setDigest(me?.task_digest_enabled !== false);
    setIdReport(me?.id_report_enabled !== false);
  }, [me?.name, me?.time_zone, me?.tax_agent_number, me?.task_digest_enabled, me?.id_report_enabled]);

  const guessed = guessBrowserTz();
  const tzOptions = (() => {
    const set = new Set(TIMEZONES);
    if (guessed) set.add(guessed);
    if (tz) set.add(tz);
    return Array.from(set).sort();
  })();

  const onSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await axios.patch(`${API_BASE}/auth/me`, {
        name: name.trim() || undefined,
        time_zone: tz,
        tax_agent_number: tan,
        task_digest_enabled: digest,
        id_report_enabled: idReport,
      });
      toast.success("Profile updated");
      await refresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/digest/preview`);
      setPreview(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const r = await axios.post(`${API_BASE}/digest/send-test`);
      toast.success(`Test digest sent to ${r.data.sent_to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed");
    } finally {
      setSendingTest(false);
    }
  };

  if (!me) {
    return <div className="p-8 text-[var(--ink-3)]" data-testid="profile-loading">Loading…</div>;
  }

  return (
    <div className="p-8 max-w-3xl mx-auto" data-testid="profile-page">
      <div className="mb-6">
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-[var(--ink)]">My profile</h1>
        <p className="text-[var(--ink-2)] text-sm mt-1">
          Your display name and time-zone. The time-zone drives daily timesheet reminders.
        </p>
      </div>

      <form onSubmit={onSave} className="card p-6 space-y-5" data-testid="profile-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="overline mb-1 block">Email</label>
            <div
              className="px-3 py-2 rounded-md border text-[var(--ink-3)] text-[13px] bg-[var(--subtle)]"
              style={{ borderColor: "var(--border)" }}
              data-testid="profile-email"
            >
              {me.email}
            </div>
            <div className="text-[11px] text-[var(--ink-3)] mt-1">Not editable — contact an admin to change.</div>
          </div>
          <div>
            <label className="overline mb-1 block" htmlFor="profile-name">Display name</label>
            <input
              id="profile-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              className="w-full px-3 py-2 rounded-md border bg-transparent text-[14px]"
              style={{ borderColor: "var(--border)" }}
              data-testid="profile-name"
            />
            <div className="text-[11px] text-[var(--ink-3)] mt-1 inline-flex items-center gap-1">
              <UserIcon size={11} /> Visible to teammates on tasks and assignments.
            </div>
          </div>
        </div>

        <div>
          <label className="overline mb-1 block" htmlFor="profile-tz">Time zone</label>
          <select
            id="profile-tz"
            value={tz}
            onChange={(e) => setTz(e.target.value)}
            className="w-full px-3 py-2 rounded-md border bg-transparent text-[14px]"
            style={{ borderColor: "var(--border)" }}
            data-testid="profile-time-zone"
          >
            <option value="">— Use my browser&apos;s default ({guessed || "UTC"}) —</option>
            {tzOptions.map((z) => (
              <option key={z} value={z}>{z}</option>
            ))}
          </select>
          <div className="text-[11px] text-[var(--ink-3)] mt-1 inline-flex items-center gap-1">
            <Clock size={11} /> Timesheet-incomplete reminders fire at 6 PM in this time zone (Mon-Fri). Morning digest fires at 8 AM (Mon-Fri).
          </div>
          <button
            type="button"
            onClick={openPreview}
            data-testid="profile-preview-digest"
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] border hover:bg-[var(--subtle)]"
            style={{ borderColor: "var(--border)", color: "var(--ink-2)" }}
          >
            <Eye size={12} /> Preview my morning digest
          </button>
        </div>

        <div>
          <label className="overline mb-1 block" htmlFor="profile-tan">Tax agent number</label>
          <input
            id="profile-tan"
            type="text"
            value={tan}
            onChange={(e) => setTan(e.target.value)}
            maxLength={16}
            placeholder="e.g. 25123456"
            className="w-full px-3 py-2 rounded-md border bg-transparent text-[14px] font-mono"
            style={{ borderColor: "var(--border)" }}
            data-testid="profile-tax-agent-number"
          />
          <div className="text-[11px] text-[var(--ink-3)] mt-1">Your TPB registration number. Stamped on every ID verification you approve — required before you can approve one.</div>
        </div>

        <label className="flex items-start gap-2 text-[13px] cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={digest} onChange={(e) => setDigest(e.target.checked)} data-testid="profile-task-digest" />
          <span>Morning task email<span className="block text-[11px] text-[var(--ink-3)]">At 8am your time: tasks due today plus anything overdue, with links to the original emails. Skipped on days with nothing due.</span></span>
        </label>

        <label className="flex items-start gap-2 text-[13px] cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={idReport} onChange={(e) => setIdReport(e.target.checked)} data-testid="profile-id-report" />
          <span>Weekly ID verification report<span className="block text-[11px] text-[var(--ink-3)]">Mondays at 8am your time: clients with open jobs who still aren't ID verified.</span></span>
        </label>

        <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: "var(--border)" }}>
          <div className="text-[11px] text-[var(--ink-3)]">
            Saved time zone: <span className="font-mono">{me.time_zone || "(default)"}</span>
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="profile-save"
            className="px-4 py-2 rounded-md text-[13px] font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
            style={{ background: "var(--ink)", color: "var(--paper)" }}
          >
            <Save size={13} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>

      <RateHistorySection />

      <EmailSignatureSection />

      {previewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/30"
          onClick={() => setPreviewOpen(false)}
          data-testid="digest-preview-modal"
        >
          <div
            className="w-full sm:max-w-2xl max-h-[90vh] overflow-hidden rounded-t-lg sm:rounded-lg shadow-xl flex flex-col"
            style={{ background: "var(--paper)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "var(--border)" }}>
              <div>
                <div className="overline">Digest preview</div>
                <div className="text-[14px] font-medium text-[var(--ink)]">
                  {preview?.subject || "Your morning digest"}
                </div>
                {preview && (
                  <div className="text-[11px] text-[var(--ink-3)] mt-0.5" data-testid="digest-preview-hits">
                    {preview.hit_count} item(s) · rendered for {preview.rendered_at_local}
                  </div>
                )}
              </div>
              <button
                onClick={() => setPreviewOpen(false)}
                className="text-[var(--ink-3)] hover:text-[var(--ink)]"
                data-testid="digest-preview-close"
              >
                <X size={18}/>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {previewLoading ? (
                <div className="text-[var(--ink-3)] text-[13px]" data-testid="digest-preview-loading">Rendering…</div>
              ) : preview?.hit_count === 0 ? (
                <div className="text-[var(--ink-3)] text-[14px] py-8 text-center" data-testid="digest-preview-empty">
                  Nothing on your plate right now — you&apos;ll skip the digest tomorrow morning.
                </div>
              ) : preview?.html ? (
                <div
                  className="prose-sm"
                  data-testid="digest-preview-html"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.html, DIGEST_HTML_CFG) }}
                />
              ) : null}
            </div>
            {!previewLoading && preview && (
              <div className="border-t px-5 py-3 flex items-center justify-between gap-2" style={{ borderColor: "var(--border)" }}>
                <div className="text-[11px] text-[var(--ink-3)]">
                  Sends to <span className="font-mono">{me.email}</span>
                </div>
                <button
                  type="button"
                  onClick={sendTestEmail}
                  disabled={sendingTest}
                  data-testid="digest-send-test"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: "var(--ink)", color: "var(--paper)" }}
                >
                  <Send size={12} /> {sendingTest ? "Sending…" : "Send me a test email"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// v68.10 — "Your rate history" — visible to every user, shows how their
// stage_role's rate has changed across FY plans. Uses GET /rate-plans/my-history.
function RateHistorySection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/rate-plans/my-history`);
        setData(r.data);
      } catch { /* silent — this section is progressive enhancement */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !data) return null;

  return (
    <div className="card p-6 mt-6" data-testid="profile-rate-history">
      <div className="flex items-center gap-2 mb-1">
        <Clock size={14} className="text-[var(--ink-3)]"/>
        <div className="overline">Your rate history</div>
      </div>
      <p className="text-[13px] text-[var(--ink-2)] mb-4">
        Every rate plan the firm has ever published, filtered to your stage role.
        Spot when the new financial year&apos;s bump landed.
      </p>
      {!data.stage_role ? (
        <div className="text-[13px] text-[var(--ink-3)] py-3 border border-dashed border-[var(--border)] rounded-md text-center">
          {data.hint || "No stage role assigned."}
          {data.fallback_hourly_rate > 0 && (
            <div className="text-[11px] mt-1">
              Fallback rate on file: <span className="font-mono">${data.fallback_hourly_rate.toFixed(2)}/h</span>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="text-[11px] text-[var(--ink-3)] uppercase tracking-wider mb-2">
            Stage role: <span className="font-medium text-[var(--ink-2)]">{data.stage_role.replace(/_/g," ")}</span>
          </div>
          {data.history.length === 0 ? (
            <div className="text-[13px] text-[var(--ink-3)]">No rate plans exist for your firm yet.</div>
          ) : (
            <div className="divide-y divide-[var(--border)]">
              {data.history.map((h, i) => {
                const prev = data.history[i + 1];   // history is newest-first
                const delta = (prev && prev.my_rate != null && h.my_rate != null)
                  ? h.my_rate - prev.my_rate
                  : null;
                return (
                  <div key={h.plan_id} className="py-2.5 flex items-center gap-3 text-[13px]"
                       data-testid={`rate-history-row-${h.plan_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[var(--ink)]">{h.name}</div>
                      <div className="text-[11px] text-[var(--ink-3)] font-mono">
                        {h.start_date}{h.end_date ? ` → ${h.end_date}` : " · current"} · {h.currency}
                      </div>
                    </div>
                    {h.my_rate == null ? (
                      <span className="text-[11px] text-[var(--ink-3)] italic">no rate for your role</span>
                    ) : (
                      <div className="text-right">
                        <div className="font-mono text-[15px]">
                          ${h.my_rate.toFixed(2)}<span className="text-[10px] text-[var(--ink-3)] ml-0.5">/h</span>
                        </div>
                        {delta !== null && delta !== 0 && (
                          <div className={`text-[10px] ${delta > 0 ? "text-[var(--moss)]" : "text-[var(--accent)]"}`}>
                            {delta > 0 ? "+" : ""}${delta.toFixed(2)} vs previous
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
